import React, { useCallback, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import RubroQuotationDatesStrip from '../../../RubroQuotationDatesStrip/RubroQuotationDatesStrip.tsx'
import RubroQuotationTimeline from './RubroQuotationTimeline.tsx'
import cardStyles from './ProjectAutofillCard.module.scss'
import editorStyles from './RubroQuotationWindowSection.module.scss'
import {
  buildQuotationTimelineScale,
  computeRecommendedQuotationWindow,
  enforceMinQuotationWindowDates,
  quotationWindowMatches,
  type RecommendedQuotationWindow
} from './rubroQuotationTimelineUtils.ts'

type Props = {
  sectionId: string
  projectStartDate: string
  projectEndDate: string
  rubroStartDate: string
  rubroEndDate: string
  recommendedQuotationWindow?: RecommendedQuotationWindow
  draftLocked: boolean
  today: string
  disabledTitle?: string
  clampIsoDateToMax: (iso: string, maxIso: string) => string
  onRubroDatesChange: (patch: {
    startDate: string
    endDate: string
    datesInheritedFromProject: false
  }) => void
}

const panelMotion = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.3, ease: 'easeInOut' }
}

const RubroQuotationWindowSection: React.FC<Props> = ({
  sectionId,
  projectStartDate,
  projectEndDate,
  rubroStartDate,
  rubroEndDate,
  recommendedQuotationWindow,
  draftLocked,
  today,
  disabledTitle,
  clampIsoDateToMax,
  onRubroDatesChange
}) => {
  const [showTimelineEditor, setShowTimelineEditor] = useState(false)
  const [showExactDates, setShowExactDates] = useState(false)

  const timelineScale = useMemo(
    () => buildQuotationTimelineScale(projectStartDate, projectEndDate),
    [projectStartDate, projectEndDate]
  )

  const effectiveRecommended = useMemo(() => {
    if (recommendedQuotationWindow) return recommendedQuotationWindow
    return computeRecommendedQuotationWindow(projectStartDate, projectEndDate) ?? undefined
  }, [recommendedQuotationWindow, projectStartDate, projectEndDate])

  const showRestoreSuggestion =
    !draftLocked &&
    !!effectiveRecommended &&
    !quotationWindowMatches(
      { startDate: rubroStartDate, endDate: rubroEndDate },
      effectiveRecommended
    )

  const canUseTimelineSlider = !!timelineScale && !draftLocked

  const applyWindowDates = useCallback(
    (startDate: string, endDate: string) => {
      let nextStart = startDate
      let nextEnd = endDate
      if (projectEndDate && nextStart) {
        nextStart = clampIsoDateToMax(nextStart, projectEndDate)
      }
      if (projectEndDate && nextEnd) {
        nextEnd = clampIsoDateToMax(nextEnd, projectEndDate)
      }
      if (projectStartDate && nextStart && nextStart < projectStartDate) {
        nextStart = projectStartDate
      }
      if (nextEnd && nextStart && nextEnd < nextStart) {
        nextEnd = nextStart
      }
      const enforced = enforceMinQuotationWindowDates(
        projectStartDate,
        projectEndDate,
        nextStart,
        nextEnd
      )
      onRubroDatesChange({
        startDate: enforced.startDate,
        endDate: enforced.endDate,
        datesInheritedFromProject: false
      })
    },
    [clampIsoDateToMax, onRubroDatesChange, projectEndDate, projectStartDate]
  )

  return (
    <section
      className={`${cardStyles.projectAutofillRubroDetailBlock} ${cardStyles.projectAutofillRubroDates} ${editorStyles.root}`}
      aria-labelledby={sectionId}
    >
      <div className={cardStyles.projectAutofillRubroDatesHead}>
        <h3 id={sectionId} className={cardStyles.projectAutofillRubroDetailBlockHeading}>
          Ventana de cotización
        </h3>
        {showRestoreSuggestion ? (
          <button
            type="button"
            className={cardStyles.projectAutofillQuotationRestoreBtn}
            onClick={() =>
              applyWindowDates(effectiveRecommended!.startDate, effectiveRecommended!.endDate)
            }
          >
            Restaurar sugerencia
          </button>
        ) : null}
      </div>

      <RubroQuotationDatesStrip
        windowStartDate={rubroStartDate}
        windowEndDate={rubroEndDate}
        startLabel="Inicio"
        endLabel="Fin"
      />

      <p className={editorStyles.previewLead}>
        Así verán los cotizantes las fechas de este rubro cuando publiques la obra.
      </p>

      {canUseTimelineSlider ? (
        <button
          type="button"
          className={editorStyles.editorToggle}
          onClick={() => setShowTimelineEditor((v) => !v)}
          aria-expanded={showTimelineEditor}
        >
          {showTimelineEditor ? 'Ocultar calendario de ajuste' : 'Ajustar ventana en el calendario'}
        </button>
      ) : null}

      <AnimatePresence initial={false}>
        {showTimelineEditor && canUseTimelineSlider ? (
          <motion.div
            className={editorStyles.editorPanel}
            {...panelMotion}
          >
            <div className={editorStyles.timelineHost}>
              <RubroQuotationTimeline
                projectStartDate={projectStartDate}
                projectEndDate={projectEndDate}
                windowStartDate={rubroStartDate}
                windowEndDate={rubroEndDate}
                disabled={draftLocked}
                onWindowDatesChange={applyWindowDates}
              />
            </div>
            <button
              type="button"
              className={cardStyles.projectAutofillQuotationExactDatesToggle}
              onClick={() => setShowExactDates((v) => !v)}
              aria-expanded={showExactDates}
            >
              {showExactDates ? 'Ocultar fechas exactas' : 'Editar fechas exactas'}
            </button>
            <AnimatePresence initial={false}>
              {showExactDates ? (
                <motion.div className={editorStyles.editorPanel} {...panelMotion}>
                  <label className={cardStyles.projectAutofillRubroDateLabel}>
                    <span>Apertura del envío de presupuestos</span>
                    <input
                      type="date"
                      value={rubroStartDate || ''}
                      min={projectStartDate || today}
                      max={projectEndDate || undefined}
                      title={draftLocked ? disabledTitle : undefined}
                      onChange={(e) => {
                        let nextStart = e.target.value
                        let nextEnd = rubroEndDate
                        if (nextEnd && nextStart && nextEnd < nextStart) {
                          nextEnd = nextStart
                        }
                        applyWindowDates(nextStart, nextEnd)
                      }}
                      disabled={draftLocked}
                    />
                  </label>
                  <label className={cardStyles.projectAutofillRubroDateLabel}>
                    <span>Cierre del envío de presupuestos</span>
                    <input
                      type="date"
                      value={rubroEndDate || ''}
                      min={rubroStartDate || projectStartDate || today}
                      max={projectEndDate || undefined}
                      title={draftLocked ? disabledTitle : undefined}
                      onChange={(e) => {
                        applyWindowDates(rubroStartDate, e.target.value)
                      }}
                      disabled={draftLocked}
                    />
                  </label>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {!canUseTimelineSlider ? (
        <>
          <label className={cardStyles.projectAutofillRubroDateLabel}>
            <span>Apertura del envío de presupuestos</span>
            <input
              type="date"
              value={rubroStartDate || ''}
              min={projectStartDate || today}
              max={projectEndDate || undefined}
              title={draftLocked ? disabledTitle : undefined}
              onChange={(e) => {
                let nextStart = e.target.value
                let nextEnd = rubroEndDate
                if (nextEnd && nextStart && nextEnd < nextStart) {
                  nextEnd = nextStart
                }
                applyWindowDates(nextStart, nextEnd)
              }}
              disabled={draftLocked}
            />
          </label>
          <label className={cardStyles.projectAutofillRubroDateLabel}>
            <span>Cierre del envío de presupuestos</span>
            <input
              type="date"
              value={rubroEndDate || ''}
              min={rubroStartDate || projectStartDate || today}
              max={projectEndDate || undefined}
              title={draftLocked ? disabledTitle : undefined}
              onChange={(e) => {
                applyWindowDates(rubroStartDate, e.target.value)
              }}
              disabled={draftLocked}
            />
          </label>
        </>
      ) : null}
    </section>
  )
}

export default RubroQuotationWindowSection
